// Generated by TaroBase SDK Generator: 2026-10-04T15:00:51.103Z
// Collection: prizePoolDeposits/$depositId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Prize pool token deposits. Users donate SOL, BONK, or SKR into one of three prize pools (top1, top10, top100). Tokens accumulate in the contract vault until the monthly raffle.
 */

export interface PrizePoolDepositsRequest {
  donor: AddressType;
  poolType: string;
  tokenMint: string;
  amount: number | TimeOperation | IncrementOperation | TokenAmount;
  ts: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface PrizePoolDepositsResponse {
  donor: string;
  poolType: string;
  tokenMint: string;
  amount: number;
  ts: number;
  id: string;
  tarobase_created_at: number;
  tarobase_transaction_hash?: string | undefined; // Represents the hash of the transaction that created this item. Can be used to create Solscan links (combine with the current network in the Tarobase app)
}

/**
 * Build a PrizePoolDeposits operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildPrizePoolDeposits(depositId: string, data?: PrizePoolDepositsRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `prizePoolDeposits/${depositId}`, document: doc };
}

/**
 * Authenticated users deposit tokens into a prize pool by specifying donor (must be their wallet), poolType (top1, top10, or top100), tokenMint (SOL, BONK, SKR, MEW, or USDC), amount in base units, and ts. SECURITY: ts MUST be server time — the rule enforces @newData.ts within +/-300 seconds of @time.now, so a client cannot backdate or forward-date a deposit. Clients MUST send Time.Now for ts (never a client clock value); any other value outside the 5-minute window is rejected. This prevents shifting a deposit into a different monthly raffle period so it counts toward a raffle it did not fund. The onchain hook transfers the tokens from the user to the contract vault. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setPrizePoolDeposits(depositId: string, data?: PrizePoolDepositsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`prizePoolDeposits/${depositId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting PrizePoolDeposits: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can view all prize pool deposits to see running totals per pool.
   (Get Single Item)
 */
export async function getPrizePoolDeposits(depositId: string): Promise<PrizePoolDepositsResponse | null> {
  try {
    const result = await get(`prizePoolDeposits/${depositId}`);
    return result as PrizePoolDepositsResponse | null;
  } catch (error) {
    console.error(`Error getting PrizePoolDeposits: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single PrizePoolDeposits document. (
  Read Operation Details: Anyone can view all prize pool deposits to see running totals per pool.
  )
 */
export function subscribePrizePoolDeposits(callback: (data: PrizePoolDepositsResponse | null) => void, depositId: string): Promise<() => Promise<void>> {
  return subscribe(`prizePoolDeposits/${depositId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as PrizePoolDepositsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to PrizePoolDeposits at path '${`prizePoolDeposits/${depositId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many PrizePoolDeposits items from collection prizePoolDeposits
 
  Read Operation Details: Anyone can view all prize pool deposits to see running totals per pool.
  
 */
export async function getManyPrizePoolDeposits(filter: string = ""): Promise<PrizePoolDepositsResponse[]> {
  try {
    const results = await get("prizePoolDeposits", { prompt: filter });
    return Array.isArray(results) ? results as PrizePoolDepositsResponse[] : [];
  } catch (error) {
    console.error(`Error getting PrizePoolDeposits collection at path 'prizePoolDeposits': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in PrizePoolDeposits collection at prizePoolDeposits
 
  Read Operation Details: Anyone can view all prize pool deposits to see running totals per pool.
  
 */
export function subscribeManyPrizePoolDeposits(callback: (data: PrizePoolDepositsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("prizePoolDeposits", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as PrizePoolDepositsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to PrizePoolDeposits collection at path '${"prizePoolDeposits"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all PrizePoolDeposits items from collection prizePoolDeposits
 
  Read Operation Details: Anyone can view all prize pool deposits to see running totals per pool.
  
 */
export async function getAllPrizePoolDeposits(filter: string = ""): Promise<PrizePoolDepositsResponse[]> {
  try {
    const results = await get("prizePoolDeposits", { prompt: filter });
    return Array.isArray(results) ? results as PrizePoolDepositsResponse[] : [];
  } catch (error) {
    console.error(`Error getting PrizePoolDeposits collection at path 'prizePoolDeposits': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in PrizePoolDeposits collection at prizePoolDeposits
 
  Read Operation Details: Anyone can view all prize pool deposits to see running totals per pool.
  
 */
export function subscribeAllPrizePoolDeposits(callback: (data: PrizePoolDepositsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("prizePoolDeposits", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as PrizePoolDepositsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to PrizePoolDeposits collection at path '${"prizePoolDeposits"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count PrizePoolDeposits items in collection prizePoolDeposits.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countPrizePoolDeposits(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("prizePoolDeposits", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting PrizePoolDeposits at path 'prizePoolDeposits': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on PrizePoolDeposits items in collection prizePoolDeposits.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregatePrizePoolDeposits(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("prizePoolDeposits", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating PrizePoolDeposits at path 'prizePoolDeposits': ${formatError(error)}`);
    return { value: 0 };
  }
}

